import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import "core-js/modules/web.dom-collections.iterator.js";
import T from 'prop-types';
import { Children, cloneElement, Component, createRef } from 'react';
import styled from 'styled-components';
import { contains, isFocused } from '../../helpers/dom';
import { Menu as DropdownMenuList, MenuItem as DropdownMenuItem } from '../Menu/Menu';
import { modifiers, Tether } from '../Tether/Tether';
import { jsx as _jsx } from "react/jsx-runtime";
const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "svf82h-0"
})(["display:inline-block;"]);
let instanceId = 0;

class DropdownMenu extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      focused: false,
      open: false
    };
    this.instanceId = instanceId++;
    this.toggleElement = /*#__PURE__*/createRef();
    this.menuElement = /*#__PURE__*/createRef();

    this.clickedOutside = event => !contains(this.toggleElement.current, event.target) && !contains(this.menuElement.current, event.target);

    this.isFocused = () => isFocused(this.menuElement.current) || isFocused(this.toggleElement.current);

    this.focusToggle = () => {
      if (this.toggleElement.current && this.toggleElement.current.querySelector('button')) {
        this.toggleElement.current.querySelector('button').focus();
      }
    };

    this.handleFocus = () => {
      this.setState({
        focused: true
      });
      document.addEventListener('keydown', this.handleKeyDown);
    };

    this.handleBlur = () => setTimeout(() => {
      const {
        focused
      } = this.state;

      if (focused && !this.isFocused()) {
        this.setState({
          focused: false
        });
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    }, 10);

    this.handleKeyDown = event => {
      const {
        focused,
        open
      } = this.state;

      switch (event.key) {
        case 'Escape':
        case 'Tab':
          this.focusToggle();
          this.closeMenu();
          break;

        case 'ArrowDown':
        case 'ArrowUp':
          if (focused) {
            if (!open) {
              this.openMenu(event.key === 'ArrowDown' ? 1 : -1);
            } else {
              this.moveMenuItemFocus(event.key === 'ArrowDown' ? 1 : -1);
            }
          }

          break;

        case 'Enter':
        case ' ':
          if (this.toggleElement.current === event.target.parentNode) {
            event.preventDefault();
            event.stopPropagation();
            this.openMenu(1);
          }

          if (event.target && event.target.getAttribute('role') === 'menuitem') {
            event.target.click();
          }

          break;

        default: // do nothing

      }
    };

    this.handleDocumentClick = event => {
      if (this.clickedOutside(event)) {
        this.closeMenu();
      }

      if (event.target.getAttribute('role') === 'menuitem') {
        this.closeMenu();
      }
    };

    this.moveMenuItemFocus = moveBy => {
      if (!this.menuElement.current) {
        return;
      }

      const menuItems = Array.from(this.menuElement.current.querySelectorAll('[role=menuitem]'));
      const selectedIndex = menuItems.findIndex(e => e === document.activeElement);
      let nextIndex = selectedIndex + moveBy;
      if (nextIndex < 0) nextIndex = menuItems.length - 1;
      if (nextIndex > menuItems.length - 1) nextIndex = 0;
      menuItems[nextIndex].focus();
    };

    this.openMenu = moveMenuItemFocusBy => {
      const {
        onOpen
      } = this.props;
      const {
        open
      } = this.state;

      if (open) {
        return;
      }

      this.setState({
        open: true
      }, () => {
        if (onOpen) {
          onOpen();

          if (moveMenuItemFocusBy !== undefined) {
            this.moveMenuItemFocus(moveMenuItemFocusBy);
          }
        }
      });
      document.addEventListener('click', this.handleDocumentClick);
    };

    this.closeMenu = () => {
      const {
        onClose
      } = this.props;
      const {
        open
      } = this.state;

      if (!open) {
        return;
      }

      this.setState({
        open: false
      }, () => {
        if (onClose) {
          onClose();
        }
      });
      document.removeEventListener('click', this.handleDocumentClick);
    };

    this.toggleMenu = () => {
      const {
        open
      } = this.state;

      if (open) {
        this.closeMenu();
      } else {
        this.openMenu();
        this.focusToggle();
      }
    };
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleDocumentClick);
  }

  render() {
    const _this$props = this.props,
          {
      placement,
      id,
      children,
      toggle
    } = _this$props,
          restProps = _objectWithoutPropertiesLoose(_this$props, ["placement", "id", "children", "onOpen", "onClose", "toggle"]);

    const {
      open
    } = this.state;

    if (!toggle || Children.count(children) !== 1) {
      return null;
    }

    const toggleElementId = id || `dropdownMenuToggle${this.instanceId}`;
    const menuElementId = Children.only(children).props.id || `dropdownMenuMenu${this.instanceId}`;
    const toggleProps = Object.assign({}, restProps, {
      id: toggleElementId,
      ref: this.toggleElement,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      'aria-haspopup': 'true',
      'aria-expanded': open ? 'true' : 'false',
      'aria-controls': menuElementId,
      'data-parentref': 'dropdown'
    }, typeof toggle !== 'function' ? {
      onClick: this.toggleMenu
    } : {});
    return /*#__PURE__*/_jsx(Tether, {
      placement: placement,
      modifiers: [modifiers.offset],
      attachment: open ? /*#__PURE__*/cloneElement(children, {
        open,
        id: menuElementId,
        ref: this.menuElement,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        role: 'menu',
        'aria-labelledby': toggleElementId,
        'data-parentref': 'dropdown'
      }) : undefined,
      children: /*#__PURE__*/_jsx(Container, Object.assign({}, toggleProps, {
        children: typeof toggle === 'function' ? toggle({
          open,
          toggle: this.toggleMenu
        }) : toggle
      }))
    });
  }

}

const noop = () => {};

DropdownMenu.propTypes = {
  placement: T.oneOf(['bottom-start', 'bottom-end']),
  children: T.node.isRequired,
  onOpen: T.func,
  onClose: T.func,
  id: T.string,
  toggle: T.oneOfType([T.func, T.node]).isRequired
};
DropdownMenu.defaultProps = {
  placement: 'bottom-start',
  onOpen: noop,
  onClose: noop
};
export { DropdownMenu, DropdownMenuList, DropdownMenuItem };